import {formatDate, formatNumber} from '@angular/common';
import {
  AdbChartTheme,
  adbCategoryAxis,
  adbForecastSeries,
  adbGrid,
  adbLegend,
  adbPartialItem,
  adbTooltip,
  adbValueAxis
} from '@features/admin-panel/helpers/admin-dashboard-chart-theme';
import {
  AdminDashboardForecastPoint,
  AdminDashboardForecastResult,
  AdminDashboardUsageTrend,
  AdminDashboardWeeklyFlow
} from '@features/admin-panel/models/admin-dashboard-usage.models';

/*
 * ITP-E-214-02. The chart the three usage trend cards share (Projects, Scenarios, Entities; Plan ITP-P-50 SPEC WEB-3):
 * one category axis with the weeks, oldest first, then the forecast weeks; the weeks as bars stacked by layer, or as
 * one line; the current week drawn partial (faded, dashed border); the forecast as a dashed line over its 80 % band.
 */

/** The forecast series' legend name; its band series are `Forecast band base` and `Forecast band`. */
export const ADB_FORECAST_NAME = 'Forecast';

/** One stack layer (or the one line) of a usage trend chart: one value per week, in the weeks' order. */
export interface AdbUsageTrendLayer {
  name: string;
  color: string;
  values: number[];
}

/** What adbUsageTrendOption draws. */
export interface AdbUsageTrendInput {
  /** Oldest first; the last one may be the partial current week. */
  weeks: AdminDashboardWeeklyFlow[];
  forecast: AdminDashboardForecastResult | null | undefined;
  /** 'bar': the layers as stacked bars; 'line': each layer as a line (the cards pass one). */
  kind: 'bar' | 'line';
  layers: AdbUsageTrendLayer[];
  /** The tooltip HTML of week `index` (0 = the oldest week). */
  weekTooltip: (index: number) => string;
}

const day = (iso: string): string => formatDate(iso, 'd MMM', 'en-US', 'UTC');
const int = (value: number): string => formatNumber(value ?? 0, 'en-US', '1.0-0');

/** The forecast's points when it is available, else none. */
export function adbForecastPoints(forecast: AdminDashboardForecastResult | null | undefined): AdminDashboardForecastPoint[] {
  return forecast?.available ? forecast.points ?? [] : [];
}

/** "Week of 28 Sep"; the partial current week "Week of 5 Oct, so far" (`partialWord` replaces "so far"). */
export function adbUsageWeekTitle(week: AdminDashboardWeeklyFlow, partialWord = 'so far'): string {
  return week.partial ? `Week of ${day(week.weekStart)}, ${partialWord}` : `Week of ${day(week.weekStart)}`;
}

/** A forecast week's tooltip: "Week of 12 Oct: forecast 7" over a muted "80% range 5–9"; '' without a point. */
export function adbForecastTooltip(t: AdbChartTheme, point: AdminDashboardForecastPoint | undefined): string {
  if (!point) {
    return '';
  }
  return `Week of ${day(point.date)}: forecast ${int(point.value)}<br/><span style="color:${t.ink2}">80% range ${int(point.low)}–${int(point.high)}</span>`;
}

/**
 * The notes under a usage trend card (Plan ITP-P-50 story 12), in this order: "Forecast available from 23 Nov" while
 * the forecast is not available, and "Before 28 Sep: based on items that still exist" when the window holds backfilled
 * weeks (the day after `backfilledUntil`).
 */
export function adbUsageTrendNotes(trend: AdminDashboardUsageTrend | null | undefined): string[] {
  const notes: string[] = [];
  if (trend?.forecast && !trend.forecast.available && trend.forecast.availableFrom) {
    notes.push(`Forecast available from ${day(trend.forecast.availableFrom)}`);
  }
  if (trend?.backfilledUntil) {
    // The first day after the backfilled history, derived in UTC (setUTCDate, never setDate: the browser's zone would shift it).
    const firstReal = new Date(trend.backfilledUntil);
    firstReal.setUTCDate(firstReal.getUTCDate() + 1);
    notes.push(`Before ${day(firstReal.toISOString())}: based on items that still exist`);
  }
  return notes;
}

/** Nothing worth showing (ITP-E-173): no response, or no week with a value while the total is unknown or 0. */
export function adbUsageTrendEmpty(trend: AdminDashboardUsageTrend | null | undefined): boolean {
  return !trend || (!trend.total && (trend.weeks ?? []).every(week => !week.value));
}

/**
 * The ECharts option of a usage trend card: the weeks then the available forecast's weeks on one category axis, the
 * layers (bars on one stack, or lines) with the partial current week drawn by adbPartialItem, then the forecast in ink 2
 * by adbForecastSeries starting right after the last week. The tooltip of a week is the card's; a forecast week says its
 * value and 80 % range.
 */
export function adbUsageTrendOption(t: AdbChartTheme, input: AdbUsageTrendInput): Record<string, unknown> {
  const weeks = input.weeks ?? [];
  const points = adbForecastPoints(input.forecast);
  const total = weeks.length + points.length;
  const line = input.kind === 'line';
  const layers = input.layers.map(layer => ({
    name: layer.name,
    type: line ? 'line' : 'bar',
    data: Array.from({length: total}, (_, i): unknown => {
      if (i >= weeks.length) {
        return null;
      }
      const value = layer.values[i] ?? 0;
      if (!weeks[i].partial) {
        return value;
      }
      return line
        ? {value, symbol: 'circle', symbolSize: 7, itemStyle: adbPartialItem(t, layer.color)}
        : {value, itemStyle: adbPartialItem(t, layer.color)};
    }),
    ...(line
      ? {symbol: 'none', lineStyle: {width: 2, color: layer.color}, itemStyle: {color: layer.color}, areaStyle: {color: layer.color, opacity: 0.08}}
      : {stack: 'weeks', barMaxWidth: 18, itemStyle: {color: layer.color}})
  }));
  const forecast = points.length
    ? adbForecastSeries(t, ADB_FORECAST_NAME, t.ink2, weeks.length, points.map(p => ({value: p.value, low: p.low, high: p.high})), total)
    : [];
  return {
    textStyle: {fontFamily: t.font},
    grid: adbGrid(),
    legend: {...adbLegend(t), data: [...input.layers.map(layer => layer.name), ...(points.length ? [ADB_FORECAST_NAME] : [])]},
    xAxis: adbCategoryAxis(t, [...weeks.map(week => day(week.weekStart)), ...points.map(point => day(point.date))], line ? {boundaryGap: false} : {}),
    yAxis: adbValueAxis(t, (value: number) => int(value), {minInterval: 1}),
    tooltip: adbTooltip(t, {
      axisPointer: {type: line ? 'line' : 'shadow'},
      formatter: (params: any) => {
        const index: number = (Array.isArray(params) ? params[0] : params)?.dataIndex ?? 0;
        return index < weeks.length ? input.weekTooltip(index) : adbForecastTooltip(t, points[index - weeks.length]);
      }
    }),
    series: [...layers, ...forecast]
  };
}
